// Client for the durable chat-history API (/api/chat-history).
//
// WHY THIS EXISTS. Conversations used to live only in localStorage, which made
// them a property of the BROWSER rather than of the account: switching browsers
// (or clearing site data) lost every thread, silently and permanently. The
// cloud table is now the system of record and localStorage is a cache in front
// of it — see chat.js for the merge/queue side. This module is only the wire.
//
// THE CONTRACT (server side lives in the terraform repo):
//   GET    /chat-history        -> { conversations: [ {id,title,updatedAt,messages} ] }
//   GET    /chat-history/{id}   -> { conversation: {...} }   one thread in full
//   PUT    /chat-history        -> { conversations: [...] }  upsert many
//   PUT    /chat-history/{id}   -> one conversation          upsert one
//   DELETE /chat-history/{id}   -> remove it (every stored part)
// Identity is the Cognito sub the API reads off the verified ID token. The
// client never sends a user id in a body or a path, and could not usefully lie
// about one if it did. THE ONE EXCEPTION is impersonation: an owner/admin who
// is "viewing as" a roster member sends `X-Act-As: <member email>` (see
// setActingAs below), and the server — after checking the caller's role, the
// target's rank and the target's existence — keys the rows on the MEMBER's sub
// and stamps every write with who really made it. The token is still the real
// caller's; the header is a request the server is free to refuse with a 403,
// and a refusal is reported as such, never quietly answered with the caller's
// own rows (that silent substitution was the bug: "his chat" was the admin's).
// The list is complete in ids — a thread too big for the response comes back
// listed and flagged `omitted`, never absent — but bounded by COUNT at the
// handler's MAX_CONVERSATIONS; chat.js mirrors that number and treats a
// response at the cap as possibly cut.
//
// EVERY CALL TAKES AN OPTIONAL `{ token, actAs }`, and a caller whose work
// spans more than one request passes the same pair to all of them. See
// tokenFor and actingFor: that is what binds an operation to the account (and
// the member being viewed) that started it, across an account switch or an
// impersonation start/stop that happens mid-flight.
//
// NOTHING HERE THROWS. Every function feeds a render path (the chat page paints
// from cache and reconciles in the background), and a rejected promise there is
// a blank page. So each call catches its own failure and reports it in the
// return value: { ok:false, reason } means "we could not save/read", which the
// caller renders honestly and then keeps working from the cache. `ok:true` is
// only ever returned when the server actually said so.
import { getIdToken } from './cognito.js'

// Local dev has no /api proxy — the dev server's history fallback answers
// /api/* with index.html and a 200 — so, exactly like hubspot.js and data.jsx,
// the client falls through to the deployed API when the same-origin path turns
// out not to be the API. Auth still applies (the local session's Cognito token
// rides along), so this changes WHERE the request goes, never WHO may ask.
const CHAT_HISTORY_FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'

// The server refuses a body over 5MB with a 413. We stop short of that
// ourselves so an oversized write is reported as "too large to sync" here —
// with the conversation still safe in the cache — rather than as a server
// rejection the user cannot interpret.
export const MAX_BODY_CHARS = 4_500_000

/**
 * Milliseconds from whatever the wire or the cache happens to hold.
 *
 * The cache stamps `updatedAt` with Date.now() (a number); the table stores
 * UpdatedAt as ISO text. Both flow through the merge, so every comparison goes
 * through here. An unparseable value reads as 0 — the oldest possible thread —
 * so a corrupt stamp can never win a merge against a real one.
 */
export function msOf(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value) {
    const parsed = Date.parse(value)
    if (Number.isFinite(parsed)) return parsed
    const num = Number(value)
    if (Number.isFinite(num)) return num
  }
  return 0
}

/** ISO text for the wire, from either representation. */
export function isoOf(value) {
  const ms = msOf(value) || Date.now()
  try {
    return new Date(ms).toISOString()
  } catch {
    return new Date().toISOString()
  }
}

/**
 * One conversation as the API takes it. Deliberately a whitelist: a thread
 * object may pick up view-only fields over time, and storing them would grow
 * every item forever for nothing. `messages` is passed through untouched — the
 * receipts inside it are the whole point of saving a thread, and chat.js has
 * already shed anything too big to store (see fitConversation there).
 */
export function toWire(convo) {
  return {
    id: String(convo?.id || ''),
    title: String(convo?.title || ''),
    updatedAt: isoOf(convo?.updatedAt),
    messages: Array.isArray(convo?.messages) ? convo.messages : [],
  }
}

/**
 * The inverse: a stored row as the app holds it (ms stamp, array messages).
 *
 * THE `incomplete` FLAG IS LOAD-BEARING. The API answers with `messages: []`
 * in two cases that are emphatically NOT "this conversation is empty":
 *   `damaged` — a split thread is missing one of its stored parts, so the
 *               server refuses to hand back a fragment dressed up as whole;
 *   `omitted` — the thread was too big to fit in the list response and must be
 *               fetched on its own with GET /chat-history/{id}.
 * Dropping the flag here would turn either into a plausible empty thread with a
 * real timestamp, which then wins a merge against the full local copy, blanks
 * it on screen, and gets written back over the good rows the moment the user
 * types. So it is carried through, and chat.js refuses to let a row wearing it
 * overwrite or overwrite-from anything.
 */
export function fromWire(row) {
  const incomplete = row?.damaged ? 'damaged' : row?.omitted ? 'omitted' : ''
  return {
    id: String(row?.id || ''),
    ...(row?.title ? { title: String(row.title) } : {}),
    updatedAt: msOf(row?.updatedAt),
    messages: Array.isArray(row?.messages) ? row.messages : [],
    ...(incomplete
      ? {
          incomplete,
          incompleteReason: String(row?.damaged || row?.omitted || ''),
        }
      : {}),
  }
}

/**
 * THE TOKEN IS THE ACCOUNT. Resolve the one this request must ride.
 *
 * Every function here takes an optional `{ token }`, and a caller whose work
 * spans more than one request — the sync, the write queue — reads the token
 * ONCE at the start and hands that same string to each call. That is what
 * makes an operation belong to an account: getIdToken() answers with whatever
 * session is current AT AWAIT TIME, so a sign-out/sign-in that lands between
 * two calls (no reload — logout is pure SPA state) would otherwise send the
 * first user's threads to the second user's rows. A bound token cannot do
 * that: the request either goes to the account it was issued for or, if that
 * session is gone, is refused by the server.
 *
 * `''` means signed out and is answered without a request. Omitting the option
 * reads the token per call, which is right for a one-shot caller.
 */
async function tokenFor(pinned) {
  if (pinned === undefined || pinned === null) {
    try {
      return (await getIdToken()) || ''
    } catch {
      // No session to be had. Reported as `auth:false`, never as a network
      // failure — see signedOut().
      return ''
    }
  }
  return String(pinned || '')
}

// Signed out is not a failure to report as a network problem: there is no
// identity to key rows on, so we do not ask. Callers see `auth:false` and keep
// running purely from the cache without painting an error.
function signedOut() {
  return { ok: false, auth: false, reason: 'Not signed in — history stays in this browser only.' }
}

/**
 * WHO THE CALLER IS VIEWING AS — module state, set by auth.jsx's impersonation.
 *
 * `null` (the normal case) means every request is the signed-in account's own
 * and carries no extra header — byte-for-byte what the wire looked like before
 * impersonation existed. A member email means each request also carries
 * `X-Act-As: <email>`, which the server honours only for an owner/admin acting
 * on a member ranked strictly below them; everyone else gets a 403.
 *
 * Normalised the same way userScope.js normalises the chat scope (trim,
 * lowercase) so chat.js can compare the two with `===`: its pin refuses to run
 * an operation whose acting target is not the scope it is about to write into,
 * and a case difference there would void every sync while impersonating.
 */
let actingAs = null

export function setActingAs(email) {
  actingAs = String(email || '').trim().toLowerCase() || null
}

export function getActingAs() {
  return actingAs
}

/**
 * The acting target this request must carry — the `actAs` twin of tokenFor.
 *
 *   undefined → read the module state live (a one-shot caller);
 *   null      → explicitly NOT acting, whatever the module state says now;
 *   string    → act as this member, whatever the module state says now.
 *
 * The two pinned forms exist for the same reason a pinned token does: an
 * operation that started as the admin's own must not grow the header because
 * impersonation began during one of its awaits (its rows would then be read
 * from one account and written to another), and one that started while
 * viewing as a member must keep the header after "stop viewing" so its last
 * write lands where its first one did.
 */
function actingFor(pinned) {
  if (pinned === undefined) return actingAs
  if (pinned === null) return null
  return String(pinned || '').trim().toLowerCase() || null
}

/**
 * The server said no to opening that member's history.
 *
 * Reported as its own shape — `actAs` names who was asked for — so chat.js
 * can say "viewing as X, and X's history was refused" instead of the "session
 * expired" sentence a bare 403 otherwise earns. It is a terminal answer: the
 * request is NOT retried without the header and the caller's own rows are
 * NEVER fetched in the refused rows' place. Either of those would put the
 * admin's threads on screen under the member's name, which is precisely the
 * bug this header exists to fix. `serverReason` is the server's own sentence
 * on its own, for callers that want to quote it inside their own wording.
 */
function actingIgnored(res) {
  return {
    ok: false,
    status: res.status || 200,
    actAs: res.actAs,
    actingIgnored: true,
    serverReason: '',
    reason: `This workspace's history service is running an older build that cannot open ${res.actAs}'s chat — it answered with your own history instead, so nothing is shown. Deploy the chat-history API to fix it.`,
  }
}

/**
 * Either way an acting request can fail to be an acting ANSWER: refused
 * outright (403), or answered by a deployment that ignored the header. Both
 * are terminal — never retried bare, never replaced by the caller's own rows.
 * null when the response is a genuine answer for the member.
 */
function actingProblem(res) {
  if (!res || !res.actAs) return null
  if (res.status === 403) return actingRefused(res)
  if (res.actingIgnored) return actingIgnored(res)
  return null
}

function actingRefused(res) {
  const served = String(res?.data?.error || '').trim()
  return {
    ok: false,
    status: 403,
    actAs: res.actAs,
    serverReason: served,
    reason: `The server refused to open ${res.actAs}'s history${served ? ` (${served})` : ''}.`,
  }
}

/**
 * One request, with the same-origin-then-deployed fallback every client in this
 * app uses. Returns { ok, status, data } — plus `actAs` when the request
 * carried the X-Act-As header — and never throws; `status:0` means we never
 * got an answer at all (offline, DNS, CORS), which is a different fact from a
 * status the server chose to send.
 */
// No request may hang the queue. The sync drains one thread at a time behind
// an in-flight flag; a fetch that never settles (a stalled connection, a proxy
// that swallows a large PUT) would hold that flag forever and every later
// write in the session would silently wait behind it. So every request is
// aborted after this long and reported as unreachable — which the queue
// retries, exactly like any other network failure.
export const REQUEST_TIMEOUT_MS = 30_000

function fetchWithTimeout(url, init, ms = REQUEST_TIMEOUT_MS) {
  if (typeof AbortController !== 'function') return fetch(url, init)
  const ctl = new AbortController()
  const timer = setTimeout(() => ctl.abort(), ms)
  return fetch(url, { ...init, signal: ctl.signal }).finally(() => clearTimeout(timer))
}

async function request(path, { method = 'GET', body = null, token = null, actAs } = {}) {
  const bearer = await tokenFor(token)
  if (!bearer) return { ok: false, status: 0, data: null, auth: false }
  const acting = actingFor(actAs)
  const headers = {
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${bearer}`,
    // Only ever present while viewing as a member. A request without it is
    // exactly the request this module has always sent.
    ...(acting ? { 'X-Act-As': acting } : {}),
  }
  const init = { method, headers, ...(body ? { body } : {}) }
  const res = await exchange(path, init)
  if (!acting) return res
  // The answer remembers who it was asked for, so a 403 can be told apart from
  // an expired session by the function that turns it into a result.
  //
  // AND WHETHER THE SERVER ACTUALLY DID IT. An older deployment does not
  // reject `X-Act-As`, it ignores it — 200, keyed on our own sub, answered
  // with our OWN threads. Believing that 200 is how the owner ends up looking
  // at 43 of their own conversations under the member's name, which is the
  // bug the header exists to fix. So a successful acting response must SAY
  // whose rows it served (handler.py's `actingAs`), and one that does not is
  // treated as a refusal, not as an answer.
  const echoed = String(res?.data?.actingAs || '').trim().toLowerCase()
  const honoured = echoed === acting
  return { ...res, actAs: acting, ...(res.ok && !honoured ? { actingIgnored: true } : {}) }
}

// The wire itself: one init, sent as-is to the same-origin path and then, if
// that was not the API, to the deployed one. The SAME init both times — the
// fallback changes where the request goes, never what it says, so an acting
// request that falls through still acts.
async function exchange(path, init) {
  let res
  try {
    res = await fetchWithTimeout(`/api/chat-history${path}`, init)
  } catch {
    // The same-origin path is not reachable at all. The deployed API still
    // might be (local dev with no proxy), so fall through rather than give up.
    res = null
  }
  let ct = res ? res.headers.get('content-type') || '' : ''
  if ((!res || !ct.includes('application/json')) && CHAT_HISTORY_FALLBACK_BASE) {
    try {
      res = await fetchWithTimeout(`${CHAT_HISTORY_FALLBACK_BASE}/chat-history${path}`, init)
      ct = res.headers.get('content-type') || ''
    } catch {
      return { ok: false, status: 0, data: null }
    }
  }
  if (!res) return { ok: false, status: 0, data: null }
  // A 204 is a legitimate empty success (DELETE); anything else that is not
  // JSON is the dev server's index.html or an edge error page, never an answer.
  if (res.status === 204) return { ok: true, status: 204, data: {} }
  if (!ct.includes('application/json')) {
    return { ok: false, status: res.status, data: null }
  }
  let data = null
  try {
    data = await res.json()
  } catch {
    return { ok: false, status: res.status, data: null }
  }
  return { ok: res.ok, status: res.status, data }
}

// The message a caller shows. The server's own `error` wins when it sent one —
// it knows why it said no — and each status we can name gets a sentence that
// says what actually happened instead of "sync failed".
function reasonFor(res) {
  const served = String(res?.data?.error || '').trim()
  if (served) return served
  if (!res || res.status === 0) return 'History could not be reached from this browser.'
  if (res.status === 401 || res.status === 403) return 'Your session expired — sign in again to sync history.'
  if (res.status === 413) return 'This conversation is too large to save to your account.'
  return `History sync failed (${res.status}).`
}

/**
 * Every conversation this account holds. Resolves { ok:true, conversations }
 * or { ok:false, reason } — and a failure is NEVER an empty list, because
 * "we could not ask" and "you have no history" must not be confused: the
 * caller would wipe a perfectly good cache on the first flaky request.
 */
export async function listRemote({ token = null, actAs } = {}) {
  const res = await request('', { token, actAs })
  if (res.auth === false) return signedOut()
  const acted = actingProblem(res)
  if (acted) return acted
  if (!res.ok) return { ok: false, reason: reasonFor(res), status: res.status }
  const rows = Array.isArray(res.data?.conversations) ? res.data.conversations : []
  return { ok: true, conversations: rows.map(fromWire).filter((c) => c.id) }
}

/**
 * One conversation in full: GET /chat-history/{id}.
 *
 * The list response is size-bounded, so a very large thread comes back from
 * `listRemote` as metadata plus `incomplete:'omitted'`. This is how it is then
 * recovered — one thread on its own, where the size budget is not in play.
 * Resolves { ok:true, conversation } or { ok:false, reason }; a 404 is reported
 * as `missing` rather than as an empty conversation, because those are
 * different facts and only one of them may touch the cache.
 */
export async function getRemote(id, { token = null, actAs } = {}) {
  const key = String(id || '')
  if (!key) return { ok: false, reason: 'That conversation has no id.' }
  const res = await request(`/${encodeURIComponent(key)}`, { token, actAs })
  if (res.auth === false) return signedOut()
  const acted = actingProblem(res)
  if (acted) return acted
  if (res.status === 404) return { ok: false, missing: true, reason: 'No such conversation.' }
  if (!res.ok) return { ok: false, reason: reasonFor(res), status: res.status }
  const row = res.data?.conversation
  if (!row || typeof row !== 'object') {
    return { ok: false, reason: 'History sent an answer this browser could not read.' }
  }
  return { ok: true, conversation: fromWire(row) }
}

/** Upsert one conversation — the hot path, one thread per keystroke burst. */
export async function putRemote(convo, { token = null, actAs } = {}) {
  const wire = toWire(convo)
  if (!wire.id) return { ok: false, reason: 'That conversation has no id.' }
  const body = JSON.stringify(wire)
  if (body.length > MAX_BODY_CHARS) {
    return {
      ok: false,
      tooLarge: true,
      reason: 'This conversation is too large to save to your account.',
    }
  }
  const res = await request(`/${encodeURIComponent(wire.id)}`, { method: 'PUT', body, token, actAs })
  if (res.auth === false) return signedOut()
  const acted = actingProblem(res)
  if (acted) return acted
  if (!res.ok) return { ok: false, reason: reasonFor(res), status: res.status }
  return { ok: true, saved: Number(res.data?.saved) || 1 }
}

/**
 * Upsert many — the migration path, and any bulk reconcile.
 *
 * Chunked by SIZE, not by count: one 149KB thread and thirty small ones are
 * both legitimate, and only the byte total can decide how many fit in a
 * request. Every chunk must succeed for this to report ok, and `saved` counts
 * what the server actually acknowledged — a partial success reports the count
 * it really achieved alongside ok:false, so the caller does not mark a
 * migration finished that only half happened.
 */
export async function putRemoteMany(convos, { token = null, actAs } = {}) {
  const list = (Array.isArray(convos) ? convos : []).map(toWire).filter((c) => c.id)
  if (!list.length) return { ok: true, saved: 0 }

  const chunks = []
  let current = []
  let size = 2
  for (const convo of list) {
    const len = JSON.stringify(convo).length + 1
    if (current.length && size + len > MAX_BODY_CHARS) {
      chunks.push(current)
      current = []
      size = 2
    }
    current.push(convo)
    size += len
  }
  if (current.length) chunks.push(current)

  let saved = 0
  for (const chunk of chunks) {
    const body = JSON.stringify({ conversations: chunk })
    if (body.length > MAX_BODY_CHARS) {
      // A single conversation bigger than a whole request. chat.js sheds before
      // it gets here, so this is the honest floor, not a silent drop.
      return { ok: false, saved, tooLarge: true, reason: 'A conversation is too large to save.' }
    }
    const res = await request('', { method: 'PUT', body, token, actAs })
    if (res.auth === false) return { ...signedOut(), saved }
    const acted = actingProblem(res)
    if (acted) return { ...acted, saved }
    if (!res.ok) return { ok: false, saved, reason: reasonFor(res), status: res.status }
    saved += Number(res.data?.saved) || chunk.length
  }
  return { ok: true, saved }
}

/**
 * Remove one conversation and every part it was split into.
 *
 * A 404 counts as success: the row is gone, which is what was asked for. Any
 * other failure keeps the delete queued (chat.js retries) so a thread the user
 * removed cannot come back on the next device's sync.
 */
export async function deleteRemote(id, { token = null, actAs } = {}) {
  const key = String(id || '')
  if (!key) return { ok: false, reason: 'That conversation has no id.' }
  const res = await request(`/${encodeURIComponent(key)}`, { method: 'DELETE', token, actAs })
  if (res.auth === false) return signedOut()
  const acted = actingProblem(res)
  if (acted) return acted
  if (res.status === 404) return { ok: true, missing: true }
  if (!res.ok) return { ok: false, reason: reasonFor(res), status: res.status }
  return { ok: true }
}
